import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import { ErrorMessage, Field, Form, Formik } from 'formik';
import * as Yup from 'yup';
import CustomLoader from '../../common/CustomLoader';
import { drapcodeIcon, iconForField } from '../../../common/custom/icon-library';
import CustomRadio from '../../../common/ui/CustomRadio';
import TooltipInfo from '../../../common/docs/TooltipInfo';
import { showAlertHtml } from '../../../common/utils/alert';
import FormFieldWithLabel from '../../common/FormFieldWithLabel';
import { inUseDescription, typeDescription } from '../../../common/utils/validate';
class CollectionForm extends Component {
  static propTypes = {
    actions: PropTypes.object.isRequired,
    commonActions: PropTypes.object.isRequired,
    projectId: PropTypes.string.isRequired,
    collection: PropTypes.object.isRequired,
    projectCollections: PropTypes.array.isRequired,
  };

  state = {
    isPersistent: false,
    collectionSourceOfOrigin: 'DEFAULT',
    inUse: true,
  };

  componentDidMount() {
    const { collection } = this.props;
    this.setState({
      isPersistent: !!collection.willPersist,
      inUse: !!collection.inUse,
      collectionSourceOfOrigin: collection.sourceOfOrigin ? collection.sourceOfOrigin : 'DEFAULT',
    });
  }

  handleCheckboxChange = (event) => {
    const { name, checked } = event.target;
    if (name === 'willPersist') {
      const confirmationMsg = checked
        ? `The DrapCode (default) database will be used to store and retrieve data.<br/><br/>Do you want to proceed?`
        : `Data will be fetched from an external source.<br/>Hence, you will <strong>not</strong> be able to store item data in the DrapCode (default) database.<br/><br/>Do you want to proceed?`;

      if (checked) {
        const preConfirm = () => {
          this.setState({ isPersistent: checked });
        };
        showAlertHtml(confirmationMsg, preConfirm, 'Proceed');
      } else {
        const preConfirm = () => {
          this.setState({ isPersistent: checked });
        };
        showAlertHtml(confirmationMsg, preConfirm, 'Proceed');
      }
    } else {
      this.setState({ [name]: checked });
    }
  };

  handleChange = (e) => {
    let { name, value } = e.target;
    if (name === 'sourceOfOrigin') {
      this.setState({ collectionSourceOfOrigin: value });
    } else {
      this.setState({ [name]: value });
    }
  };

  handleSubmit = (values, setErrors, setSubmitting) => {
    setSubmitting(false);
    this.saveOrUpdate(values);
  };

  saveOrUpdate = (values) => {
    const { collection, projectId, version, actions, commonActions } = this.props;
    const { fields, uuid, _id, collectionName } = collection;
    if (uuid) {
      const data = {
        collectionTitle: values.collectionTitle,
        collectionIndex: values.collectionIndex,
        willPersist: values.willPersist,
        projectId: this.props.projectId,
        fields: fields,
        inUse: values.inUse,
        description: values.description,
      };
      actions.updateCollections({
        collectionId: uuid,
        data: data,
        projectId,
        version,
      });
      commonActions.trackCollectionChange({ uuid: uuid, name: collectionName });
    }
  };

  prepareValidationForFields = () => {
    let validationSchema = {
      collectionTitle: Yup.string()
        .required("Collection title can't be blank")
        .matches(/^[A-Za-z\s]+$/, 'Name should not contain special characters or numbers')
        .max(100, 'Title can not be more than 100 characters'),
    };
    return Yup.object().shape(validationSchema);
  };

  initialSchema = () => {
    const { collection } = this.props;
    const { isPersistent, inUse } = this.state;
    return {
      collectionTitle: collection.collectionTitle,
      willPersist: isPersistent,
      collectionIndex: collection.collectionIndex
        ? collection.collectionIndex
        : Math.floor(Math.random() * this.props.projectCollections.length + 1),
      description: collection.description,
      inUse,
    };
  };

  render() {
    const { uuid } = this.props.collection;
    const { isPersistent, inUse } = this.state;
    const showCollectionLoader = this.props.showCollectionLoader;

    return (
      <Fragment>
        <Formik
          enableReinitialize={true}
          initialValues={this.initialSchema()}
          validationSchema={this.prepareValidationForFields()}
          onSubmit={(values, { setSubmitting, resetForm, setErrors }) => {
            this.handleSubmit(values, setErrors, setSubmitting, resetForm);
          }}
        >
          {({ isSubmitting, values, errors }) => (
            <Form className="form-horizontal">
              <div className="row mx-auto">
                <div className="col-md-12 col-lg-12 col-sm-12 col-xs-12">
                  <div className="form-group">
                    <FormFieldWithLabel
                      cta={false}
                      label="Collection Name"
                      icon="text"
                      infoIcon={true}
                      isRequired={true}
                      infoDescription={
                        'Update Collection Name of your choice. It should not contain special characters or numbers.'
                      }
                    >
                      <Field
                        type="text"
                        name="collectionTitle"
                        className="form-control form-control-sm"
                        placeholder="Collection Name"
                        title="Enter Collection Name"
                      />
                      <ErrorMessage name="collectionTitle" component="p" className="text-danger" />
                    </FormFieldWithLabel>
                  </div>
                  <div className="form-group">
                    <FormFieldWithLabel
                      cta={false}
                      label="Description (Optional)"
                      icon="text"
                      infoIcon={true}
                      infoDescription={'Explains the purpose or usage of the collection.'}
                    >
                      <Field
                        type="text"
                        name="description"
                        className="form-control form-control-sm"
                        placeholder="Description For Collection"
                      />
                      <ErrorMessage name="description" component="p" className="text-danger" />
                    </FormFieldWithLabel>
                  </div>
                  <div className="form-group">
                    <div className="d-flex align-items-center">
                      <div>
                        <div className="input-group" title="Persistent Collection">
                          <div className="input-group-prepend">
                            <span className="input-group-text">
                              <i className="wb webconnect-toggles"></i>
                              <span className="required">*</span>
                            </span>
                          </div>
                          <div className="form-check">
                            <CustomRadio label="Persistent Collection" isRadio={false}>
                              <input
                                name="willPersist"
                                type="checkbox"
                                className="form-check-input"
                                checked={isPersistent}
                                onChange={this.handleCheckboxChange}
                              />
                            </CustomRadio>
                          </div>
                          <TooltipInfo
                            title={typeDescription}
                            headerText={`What's Persistent Collection?`}
                            showHeader={true}
                            showArrow={true}
                            append={true}
                          />
                        </div>
                      </div>
                      <div>
                        <div className="input-group" title="In Use">
                          <div className="input-group-prepend">
                            <span className="input-group-text">
                              <i className="wb webconnect-toggles"></i>
                              {/* <span className="required">*</span> */}
                            </span>
                          </div>
                          <div className="form-check">
                            <CustomRadio label="In Use" isRadio={false}>
                              <input
                                name="inUse"
                                type="checkbox"
                                className="form-check-input"
                                checked={inUse}
                                onChange={this.handleCheckboxChange}
                              />
                            </CustomRadio>
                          </div>
                          <TooltipInfo
                            title={inUseDescription}
                            headerText={`What's In Use?`}
                            showHeader={true}
                            showArrow={true}
                            append={true}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="col-md-12 col-lg-12 text-right">
                <button
                  type="submit"
                  className="btn btn-theme-submit btn-md"
                  disabled={isSubmitting || showCollectionLoader}
                >
                  <i
                    className={
                      uuid === undefined ? 'wb webconnect-plus' : 'wb webconnect-save-regular'
                    }
                  ></i>
                  &nbsp;
                  {uuid === undefined ? '  Create' : 'Update'}&nbsp;
                  <CustomLoader loading={showCollectionLoader} />
                </button>
              </div>
            </Form>
          )}
        </Formik>
      </Fragment>
    );
  }
}

export default CollectionForm;
